import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, LogOut, Mail, CalendarCheck, Quote, Newspaper, FileText, Lock, Send, Phone, MessageCircle, Instagram } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { ImageUploadField } from "@/components/ImageUploadField";

const empty = { title: "", excerpt: "", content: "", cover_image: "", category: "Mental Health", author: "Dr. Manas Team" };
const emptyTestimonial = { name: "", location: "", role: "", quote: "", rating: 5, order: 100, category: "general" };
const emptyPress = { eyebrow: "", title: "", description: "", image_url: "", publication: "", size: "md", order: 100 };
const emptyContent = { type: "case_report", title: "", excerpt: "", content: "", cover_image: "", tag: "", author: "Dr. Manas Team", external_url: "" };
const emptyInsta = { image_url: "", caption: "", post_url: "", order: 100 };

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [appts, setAppts] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [settings, setSettings] = useState({ google_rating: 0, google_review_count: 0, google_review_url: "" });
  const [testimonials, setTestimonials] = useState([]);
  const [pressItems, setPressItems] = useState([]);
  const [instaItems, setInstaItems] = useState([]);
  const [iEditing, setIEditing] = useState(null);
  const [iForm, setIForm] = useState(emptyInsta);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  // Testimonial editor state
  const [tEditing, setTEditing] = useState(null);
  const [tForm, setTForm] = useState(emptyTestimonial);

  // Press editor state
  const [prEditing, setPrEditing] = useState(null);
  const [prForm, setPrForm] = useState(emptyPress);

  // Content (case reports & articles) state
  const [contentItems, setContentItems] = useState([]);
  const [contentType, setContentType] = useState("case_report");
  const [cEditing, setCEditing] = useState(null);
  const [cForm, setCForm] = useState(emptyContent);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/admin/login");
    }
  }, [loading, user, navigate]);

  const refresh = async () => {
    try {
      const [p, a, m, t, pr, c, en, st, ig] = await Promise.all([
        api.get("/blog/posts"),
        api.get("/appointments"),
        api.get("/contact"),
        api.get("/testimonials"),
        api.get("/press"),
        api.get("/content"),
        api.get("/enquiries"),
        api.get("/settings"),
        api.get("/instagram"),
      ]);
      setPosts(p.data);
      setAppts(a.data);
      setMsgs(m.data);
      setTestimonials(t.data);
      setPressItems(pr.data);
      setInstaItems(ig.data);
      setContentItems(c.data);
      setEnquiries(en.data);
      setSettings({
        google_rating: st.data.google_rating || 0,
        google_review_count: st.data.google_review_count || 0,
        google_review_url: st.data.google_review_url || "",
      });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    if (user) refresh();
  }, [user]);

  const openNew = () => {
    setForm(empty);
    setEditing({});
  };

  const openEdit = (post) => {
    setForm({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      cover_image: post.cover_image || "",
      category: post.category || "Mental Health",
      author: post.author || "Dr. Manas Team",
    });
    setEditing(post);
  };

  const savePost = async (e) => {
    e.preventDefault();
    if (!form.title || !form.excerpt || !form.content) {
      toast.error("Title, excerpt and content are required.");
      return;
    }
    setSaving(true);
    try {
      if (editing && editing.id) {
        await api.put(`/blog/posts/${editing.id}`, form);
        toast.success("Post updated");
      } else {
        await api.post("/blog/posts", form);
        toast.success("Post published");
      }
      setEditing(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await api.delete(`/blog/posts/${id}`);
      toast.success("Post deleted");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/appointments/${id}?status=${status}`);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // ---- Testimonial helpers ----
  const openNewT = () => { setTForm(emptyTestimonial); setTEditing({}); };
  const openEditT = (t) => {
    setTForm({ name: t.name, location: t.location, role: t.role, quote: t.quote, rating: t.rating, order: t.order, category: t.category || "general" });
    setTEditing(t);
  };
  const saveT = async (e) => {
    e.preventDefault();
    if (!tForm.name || !tForm.quote) {
      toast.error("Name and quote are required.");
      return;
    }
    setSaving(true);
    try {
      if (tEditing && tEditing.id) {
        await api.put(`/testimonials/${tEditing.id}`, tForm);
        toast.success("Testimonial updated");
      } else {
        await api.post("/testimonials", tForm);
        toast.success("Testimonial added");
      }
      setTEditing(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  const deleteT = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await api.delete(`/testimonials/${id}`);
      toast.success("Testimonial deleted");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // ---- Press helpers ----
  const openNewPr = () => { setPrForm(emptyPress); setPrEditing({}); };
  const openEditPr = (p) => {
    setPrForm({
      eyebrow: p.eyebrow, title: p.title, description: p.description,
      image_url: p.image_url, publication: p.publication || "",
      size: p.size || "md", order: p.order || 100,
    });
    setPrEditing(p);
  };
  const savePr = async (e) => {
    e.preventDefault();
    if (!prForm.title || !prForm.image_url) {
      toast.error("Title and image URL are required.");
      return;
    }
    setSaving(true);
    try {
      if (prEditing && prEditing.id) {
        await api.put(`/press/${prEditing.id}`, prForm);
        toast.success("Press item updated");
      } else {
        await api.post("/press", prForm);
        toast.success("Press item added");
      }
      setPrEditing(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  const deletePr = async (id) => {
    if (!window.confirm("Delete this press item?")) return;
    try {
      await api.delete(`/press/${id}`);
      toast.success("Press item deleted");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // ---- Instagram highlight helpers ----
  const openNewI = () => { setIForm(emptyInsta); setIEditing({}); };
  const openEditI = (p) => {
    setIForm({ image_url: p.image_url, caption: p.caption || "", post_url: p.post_url || "", order: p.order || 100 });
    setIEditing(p);
  };
  const saveI = async (e) => {
    e.preventDefault();
    if (!iForm.image_url) {
      toast.error("Please add an image for the post.");
      return;
    }
    setSaving(true);
    try {
      if (iEditing && iEditing.id) {
        await api.put(`/instagram/${iEditing.id}`, iForm);
        toast.success("Instagram post updated");
      } else {
        await api.post("/instagram", iForm);
        toast.success("Instagram post added");
      }
      setIEditing(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  const deleteI = async (id) => {
    if (!window.confirm("Remove this post from the homepage?")) return;
    try {
      await api.delete(`/instagram/${id}`);
      toast.success("Removed");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // ---- Content (case reports & articles) helpers ----
  const openNewC = () => {
    setCForm({ ...emptyContent, type: contentType });
    setCEditing({});
  };
  const openEditC = (c) => {
    setCForm({
      type: c.type, title: c.title, excerpt: c.excerpt, content: c.content,
      cover_image: c.cover_image || "", tag: c.tag || "",
      author: c.author || "Dr. Manas Team", external_url: c.external_url || "",
    });
    setCEditing(c);
  };
  const saveC = async (e) => {
    e.preventDefault();
    if (!cForm.title || !cForm.excerpt || !cForm.content) {
      toast.error("Title, excerpt and content are required.");
      return;
    }
    setSaving(true);
    try {
      if (cEditing && cEditing.id) {
        await api.put(`/content/${cEditing.id}`, cForm);
        toast.success(`${cForm.type === "case_report" ? "Case report" : "Article"} updated`);
      } else {
        await api.post("/content", cForm);
        toast.success(`${cForm.type === "case_report" ? "Case report" : "Article"} published`);
      }
      setCEditing(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  const deleteC = async (id) => {
    if (!window.confirm("Delete this item permanently?")) return;
    try {
      await api.delete(`/content/${id}`);
      toast.success("Deleted");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // ---- Enquiry reply ----
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");

  const openReply = (e) => {
    setReplyText(e.reply || "");
    setReplyTo(e);
  };

  const sendReply = async (ev) => {
    ev.preventDefault();
    if (replyText.trim().length < 5) {
      toast.error("Please write a longer reply.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/enquiries/${replyTo.id}/reply`, { message: replyText });
      toast.success("Reply emailed privately");
      setReplyTo(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveSettings = async (ev) => {
    ev.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", {
        google_rating: Number(settings.google_rating) || 0,
        google_review_count: Number(settings.google_review_count) || 0,
        google_review_url: settings.google_review_url,
      });
      toast.success("Google rating updated");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteEnquiry = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await api.delete(`/enquiries/${id}`);
      toast.success("Enquiry deleted");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading || !user) {
    return <div className="min-h-[60vh] flex items-center justify-center text-[#A39171]">Loading…</div>;
  }

  return (
    <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-6 md:px-10 py-12">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10">
        <div>
          <p className="eyebrow text-[#A39171] mb-3">Admin Console</p>
          <h1 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight">Manage Manas</h1>
        </div>
        <Button data-testid="admin-logout" onClick={logout} variant="outline" className="rounded-full border-[#A39171]/40 hover:bg-[#E07A5F] hover:text-white hover:border-[#E07A5F]">
          <LogOut className="w-4 h-4 mr-2" /> Sign out
        </Button>
      </div>

      <Tabs defaultValue="blog">
        <TabsList className="bg-[#F4EFE6] rounded-full p-1 h-12 mb-8 flex-wrap">
          <TabsTrigger value="blog" data-testid="tab-blog" className="rounded-full data-[state=active]:bg-[#2D2623] data-[state=active]:text-white px-6">Blog</TabsTrigger>
          <TabsTrigger value="appointments" data-testid="tab-appointments" className="rounded-full data-[state=active]:bg-[#2D2623] data-[state=active]:text-white px-6">Appointments</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages" className="rounded-full data-[state=active]:bg-[#2D2623] data-[state=active]:text-white px-6">Messages</TabsTrigger>
          <TabsTrigger value="enquiries" data-testid="tab-enquiries" className="rounded-full data-[state=active]:bg-[#2D2623] data-[state=active]:text-white px-6">Confidential Enquiries</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings" className="rounded-full data-[state=active]:bg-[#2D2623] data-[state=active]:text-white px-6">Google Rating</TabsTrigger>
          <TabsTrigger value="testimonials" data-testid="tab-testimonials" className="rounded-full data-[state=active]:bg-[#2D2623] data-[state=active]:text-white px-6">Testimonials</TabsTrigger>
          <TabsTrigger value="press" data-testid="tab-press" className="rounded-full data-[state=active]:bg-[#2D2623] data-[state=active]:text-white px-6">Press</TabsTrigger>
          <TabsTrigger value="instagram" data-testid="tab-instagram" className="rounded-full data-[state=active]:bg-[#2D2623] data-[state=active]:text-white px-6">Instagram</TabsTrigger>
          <TabsTrigger value="content" data-testid="tab-content" className="rounded-full data-[state=active]:bg-[#2D2623] data-[state=active]:text-white px-6">Case Reports & Articles</TabsTrigger>
        </TabsList>

        <TabsContent value="blog">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-display text-2xl text-[#2D2623]">{posts.length} posts</h2>
            <Button data-testid="admin-new-post" onClick={openNew} className="rounded-full bg-[#E07A5F] hover:bg-[#2D2623] text-white">
              <Plus className="w-4 h-4 mr-2" /> New Post
            </Button>
          </div>
          <div className="grid gap-4">
            {posts.map((p) => (
              <div key={p.id} data-testid={`admin-post-${p.id}`} className="bg-[#F4EFE6] rounded-2xl p-6 border border-[#A39171]/15 flex flex-col md:flex-row justify-between gap-4">
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-[0.18em] text-[#A39171]">{p.category} · {new Date(p.created_at).toLocaleDateString()}</p>
                  <h3 className="font-display text-2xl text-[#2D2623] mt-1">{p.title}</h3>
                  <p className="text-[#5C524E] text-sm mt-2 line-clamp-2">{p.excerpt}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)} className="rounded-full border-[#A39171]/40">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => deletePost(p.id)} className="rounded-full border-[#E07A5F]/40 text-[#E07A5F] hover:bg-[#E07A5F] hover:text-white">
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="appointments">
          <h2 className="font-display text-2xl text-[#2D2623] mb-6">{appts.length} appointment requests</h2>
          <div className="grid gap-4">
            {appts.map((a) => (
              <div key={a.id} data-testid={`admin-appt-${a.id}`} className="bg-[#F4EFE6] rounded-2xl p-6 border border-[#A39171]/15">
                <div className="flex flex-col md:flex-row justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <CalendarCheck className="w-4 h-4 text-[#E07A5F]" />
                      <p className="font-display text-xl text-[#2D2623]">{a.full_name}</p>
                      <span className={`text-xs px-3 py-1 rounded-full ${a.status === "pending" ? "bg-[#E2C044]/30 text-[#2D2623]" : a.status === "confirmed" ? "bg-[#2D2623] text-white" : "bg-[#A39171]/30 text-[#2D2623]"}`}>{a.status}</span>
                    </div>
                    <p className="text-sm text-[#5C524E]"><strong>Service:</strong> {a.service}</p>
                    <p className="text-sm text-[#5C524E]"><strong>Date:</strong> {a.preferred_date} · <strong>Time:</strong> {a.preferred_time}</p>
                    <p className="text-sm text-[#5C524E]"><strong>Phone:</strong> {a.phone}{a.email ? ` · Email: ${a.email}` : ""}</p>
                    {a.message && <p className="text-sm text-[#5C524E] mt-2 italic">"{a.message}"</p>}
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    {a.status !== "confirmed" && <Button size="sm" onClick={() => updateStatus(a.id, "confirmed")} className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-white">Confirm</Button>}
                    {a.status !== "completed" && <Button size="sm" variant="outline" onClick={() => updateStatus(a.id, "completed")} className="rounded-full border-[#A39171]/40">Complete</Button>}
                    {a.status !== "cancelled" && <Button size="sm" variant="outline" onClick={() => updateStatus(a.id, "cancelled")} className="rounded-full border-[#E07A5F]/40 text-[#E07A5F] hover:bg-[#E07A5F] hover:text-white">Cancel</Button>}
                  </div>
                </div>
              </div>
            ))}
            {appts.length === 0 && <p className="text-[#A39171]">No appointment requests yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="messages">
          <h2 className="font-display text-2xl text-[#2D2623] mb-6">{msgs.length} contact messages</h2>
          <div className="grid gap-4">
            {msgs.map((m) => (
              <div key={m.id} data-testid={`admin-msg-${m.id}`} className="bg-[#F4EFE6] rounded-2xl p-6 border border-[#A39171]/15">
                <div className="flex items-center gap-3 mb-2">
                  <Mail className="w-4 h-4 text-[#E07A5F]" />
                  <p className="font-display text-xl text-[#2D2623]">{m.name}</p>
                  <span className="text-xs text-[#A39171]">{new Date(m.created_at).toLocaleString()}</span>
                </div>
                <p className="text-sm text-[#5C524E]"><strong>Subject:</strong> {m.subject}</p>
                <p className="text-sm text-[#5C524E]"><strong>Email:</strong> {m.email}{m.phone ? ` · Phone: ${m.phone}` : ""}</p>
                <p className="text-[#2D2623] mt-3">{m.message}</p>
              </div>
            ))}
            {msgs.length === 0 && <p className="text-[#A39171]">No messages yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="enquiries">
          <h2 className="font-display text-2xl text-[#2D2623] mb-6">{enquiries.length} confidential enquiries</h2>
          <div className="grid gap-4">
            {enquiries.map((e) => (
              <div key={e.id} data-testid={`admin-enquiry-${e.id}`} className="bg-[#F4EFE6] rounded-2xl p-6 border border-[#A39171]/15">
                <div className="flex flex-col md:flex-row justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Lock className="w-4 h-4 text-[#E07A5F]" />
                      <p className="font-display text-xl text-[#2D2623]">{e.topic}</p>
                      <span className="text-xs text-[#A39171]">{new Date(e.created_at).toLocaleString()}</span>
                      {e.replied_at && (
                        <span className="text-xs px-3 py-1 rounded-full bg-[#2D2623] text-white">replied</span>
                      )}
                    </div>
                    <p className="text-[#2D2623] whitespace-pre-wrap">{e.question}</p>
                    <p className="text-sm text-[#5C524E] mt-3">
                      <strong>Age/Gender:</strong> {e.age_gender || "—"} · <strong>Reply to:</strong>{" "}
                      {e.contact ? e.contact : "Anonymous (no contact given)"}
                    </p>
                    {e.reply && (
                      <p className="text-sm text-[#5C524E] mt-3 bg-[#FDFBF7] border border-[#A39171]/20 rounded-xl p-4 whitespace-pre-wrap">
                        <strong>Your reply:</strong> {e.reply}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 shrink-0 self-start">
                    {e.contact && e.contact.includes("@") && (
                      <Button size="sm" data-testid={`enquiry-reply-btn-${e.id}`} onClick={() => openReply(e)} className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-white">
                        <Send className="w-3.5 h-3.5 mr-1" /> {e.replied_at ? "Reply again" : "Reply by email"}
                      </Button>
                    )}
                    {e.contact && !e.contact.includes("@") && (
                      <>
                        <a
                          href={`tel:${e.contact.replace(/[^\d+]/g, "")}`}
                          data-testid={`enquiry-call-${e.id}`}
                          className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-white text-sm px-4 h-9 inline-flex items-center justify-center transition-colors"
                        >
                          <Phone className="w-3.5 h-3.5 mr-1" /> Call
                        </a>
                        <a
                          href={`https://wa.me/${(() => {
                            const d = e.contact.replace(/[^\d]/g, "");
                            return d.length === 10 ? `91${d}` : d;
                          })()}?text=${encodeURIComponent("Namaste, this is Manas Neuropsychiatry Hospital replying privately to the question you sent us.")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`enquiry-whatsapp-${e.id}`}
                          className="rounded-full border border-[#A39171]/40 text-[#2D2623] hover:bg-[#F4EFE6] text-sm px-4 h-9 inline-flex items-center justify-center transition-colors"
                        >
                          <MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp
                        </a>
                      </>
                    )}
                    <Button size="sm" variant="outline" onClick={() => deleteEnquiry(e.id)} className="rounded-full border-[#E07A5F]/40 text-[#E07A5F] hover:bg-[#E07A5F] hover:text-white">
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {enquiries.length === 0 && <p className="text-[#A39171]">No confidential enquiries yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="settings">
          <h2 className="font-display text-2xl text-[#2D2623] mb-2">Google rating shown on the site</h2>
          <p className="text-sm text-[#5C524E] mb-8 max-w-2xl">
            Open your Google Business Profile, note the current rating and review count, and type
            them here. The footer badge and the Contact page card update immediately. Set the rating
            to 0 to hide the number and show only a "Review us on Google" button.
          </p>
          <form onSubmit={saveSettings} className="space-y-5 max-w-xl">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-[#5C524E] mb-2 block">Rating (0 – 5)</Label>
                <Input data-testid="settings-rating" type="number" step="0.1" min="0" max="5"
                  value={settings.google_rating}
                  onChange={(e) => setSettings({ ...settings, google_rating: e.target.value })}
                  className="bg-white/70 border-[#A39171]/30 rounded-xl h-12" />
              </div>
              <div>
                <Label className="text-sm text-[#5C524E] mb-2 block">Number of reviews</Label>
                <Input data-testid="settings-review-count" type="number" min="0"
                  value={settings.google_review_count}
                  onChange={(e) => setSettings({ ...settings, google_review_count: e.target.value })}
                  className="bg-white/70 border-[#A39171]/30 rounded-xl h-12" />
              </div>
            </div>
            <div>
              <Label className="text-sm text-[#5C524E] mb-2 block">Google review link</Label>
              <Input data-testid="settings-review-url"
                value={settings.google_review_url}
                onChange={(e) => setSettings({ ...settings, google_review_url: e.target.value })}
                className="bg-white/70 border-[#A39171]/30 rounded-xl h-12" />
            </div>
            <Button data-testid="settings-save" type="submit" disabled={saving}
              className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-white px-8 h-12">
              {saving ? "Saving…" : "Save"}
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="testimonials">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-display text-2xl text-[#2D2623]">{testimonials.length} testimonials</h2>
            <Button data-testid="admin-new-testimonial" onClick={openNewT} className="rounded-full bg-[#E07A5F] hover:bg-[#2D2623] text-white">
              <Plus className="w-4 h-4 mr-2" /> New Testimonial
            </Button>
          </div>
          <div className="grid gap-4">
            {testimonials.map((t) => (
              <div key={t.id} data-testid={`admin-testimonial-${t.id}`} className="bg-[#F4EFE6] rounded-2xl p-6 border border-[#A39171]/15 flex flex-col md:flex-row justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <Quote className="w-4 h-4 text-[#E07A5F]" />
                    <p className="font-display text-xl text-[#2D2623]">{t.name}</p>
                    <span className="text-xs text-[#A39171] uppercase tracking-[0.15em]">order {t.order}</span>
                  </div>
                  <p className="text-xs uppercase tracking-[0.15em] text-[#A39171]">{t.role} · {t.location} · {t.category || "general"}</p>
                  <p className="text-[#5C524E] text-sm mt-3 line-clamp-3">{t.quote}</p>
                </div>
                <div className="flex gap-2 shrink-0 self-start">
                  <Button size="sm" variant="outline" onClick={() => openEditT(t)} className="rounded-full border-[#A39171]/40">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => deleteT(t.id)} className="rounded-full border-[#E07A5F]/40 text-[#E07A5F] hover:bg-[#E07A5F] hover:text-white">
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            ))}
            {testimonials.length === 0 && <p className="text-[#A39171]">No testimonials yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="press">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-display text-2xl text-[#2D2623]">{pressItems.length} press items</h2>
            <Button data-testid="admin-new-press" onClick={openNewPr} className="rounded-full bg-[#E07A5F] hover:bg-[#2D2623] text-white">
              <Plus className="w-4 h-4 mr-2" /> New Press Item
            </Button>
          </div>
          <div className="grid gap-4">
            {pressItems.map((p) => (
              <div key={p.id} data-testid={`admin-press-${p.id}`} className="bg-[#F4EFE6] rounded-2xl p-6 border border-[#A39171]/15 flex flex-col md:flex-row gap-5">
                {p.image_url && (
                  <div className="w-full md:w-40 aspect-[4/3] md:aspect-auto md:h-32 rounded-xl overflow-hidden bg-white shrink-0">
                    <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <Newspaper className="w-4 h-4 text-[#E07A5F]" />
                    <p className="text-xs uppercase tracking-[0.15em] text-[#A39171]">{p.eyebrow}</p>
                    <span className="text-xs text-[#A39171] uppercase tracking-[0.15em]">[{p.size}] · order {p.order}</span>
                  </div>
                  <p className="font-display text-xl text-[#2D2623] mt-0.5">{p.title}</p>
                  <p className="text-[#5C524E] text-sm mt-2 line-clamp-2">{p.description}</p>
                </div>
                <div className="flex gap-2 shrink-0 self-start">
                  <Button size="sm" variant="outline" onClick={() => openEditPr(p)} className="rounded-full border-[#A39171]/40">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => deletePr(p.id)} className="rounded-full border-[#E07A5F]/40 text-[#E07A5F] hover:bg-[#E07A5F] hover:text-white">
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            ))}
            {pressItems.length === 0 && <p className="text-[#A39171]">No press items yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="instagram">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2">
            <h2 className="font-display text-2xl text-[#2D2623]">{instaItems.length} Instagram highlights</h2>
            <Button data-testid="admin-new-insta" onClick={openNewI} className="rounded-full bg-[#E07A5F] hover:bg-[#2D2623] text-white">
              <Plus className="w-4 h-4 mr-2" /> New Instagram Post
            </Button>
          </div>
          <p className="text-sm text-[#5C524E] mb-8 max-w-2xl">
            The homepage shows the first <strong>3</strong> of these, lowest order number first. For each post:
            save the picture from Instagram, upload it here, add the caption and paste the post link so
            visitors land on the real post.
          </p>
          <div className="grid gap-4">
            {instaItems.map((p, idx) => (
              <div key={p.id} data-testid={`admin-insta-${p.id}`} className="bg-[#F4EFE6] rounded-2xl p-6 border border-[#A39171]/15 flex flex-col md:flex-row gap-5">
                {p.image_url && (
                  <div className="w-full md:w-28 h-28 rounded-xl overflow-hidden bg-white shrink-0">
                    <img src={p.image_url} alt={p.caption || "Instagram post"} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <Instagram className="w-4 h-4 text-[#E07A5F]" />
                    <span className="text-xs text-[#A39171] uppercase tracking-[0.15em]">order {p.order}</span>
                    {idx < 3 && <span className="text-xs px-3 py-1 rounded-full bg-[#2D2623] text-white">on homepage</span>}
                  </div>
                  <p className="text-[#2D2623] mt-1 line-clamp-2">{p.caption || <span className="text-[#A39171]">No caption</span>}</p>
                  {p.post_url && (
                    <a href={p.post_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#E07A5F] mt-2 inline-block">
                      Open post ↗
                    </a>
                  )}
                </div>
                <div className="flex gap-2 shrink-0 self-start">
                  <Button size="sm" variant="outline" onClick={() => openEditI(p)} className="rounded-full border-[#A39171]/40">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => deleteI(p.id)} className="rounded-full border-[#E07A5F]/40 text-[#E07A5F] hover:bg-[#E07A5F] hover:text-white">
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            ))}
            {instaItems.length === 0 && <p className="text-[#A39171]">No Instagram highlights yet — the homepage section stays hidden until you add one.</p>}
          </div>
        </TabsContent>

        <TabsContent value="content">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div className="flex items-center gap-2 bg-[#F4EFE6] rounded-full p-1">
              <button
                data-testid="content-filter-case_report"
                onClick={() => setContentType("case_report")}
                className={`px-5 h-9 rounded-full text-sm font-medium transition-all ${contentType === "case_report" ? "bg-[#2D2623] text-white" : "text-[#2D2623]"}`}
              >
                Case Reports
              </button>
              <button
                data-testid="content-filter-article"
                onClick={() => setContentType("article")}
                className={`px-5 h-9 rounded-full text-sm font-medium transition-all ${contentType === "article" ? "bg-[#2D2623] text-white" : "text-[#2D2623]"}`}
              >
                Articles
              </button>
            </div>
            <Button data-testid="admin-new-content" onClick={openNewC} className="rounded-full bg-[#E07A5F] hover:bg-[#2D2623] text-white">
              <Plus className="w-4 h-4 mr-2" /> New {contentType === "case_report" ? "Case Report" : "Article"}
            </Button>
          </div>
          <div className="grid gap-4">
            {contentItems.filter((c) => c.type === contentType).map((c) => (
              <div key={c.id} data-testid={`admin-content-${c.id}`} className="bg-[#F4EFE6] rounded-2xl p-6 border border-[#A39171]/15 flex flex-col md:flex-row justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <FileText className="w-4 h-4 text-[#E07A5F]" />
                    <p className="text-xs uppercase tracking-[0.15em] text-[#A39171]">
                      {c.tag || (c.type === "case_report" ? "Case Report" : "Article")} · {new Date(c.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <h3 className="font-display text-2xl text-[#2D2623] mt-0.5">{c.title}</h3>
                  <p className="text-[#5C524E] text-sm mt-2 line-clamp-2">{c.excerpt}</p>
                  {c.external_url && (
                    <a href={c.external_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#E07A5F] mt-2 inline-block">
                      External link ↗
                    </a>
                  )}
                </div>
                <div className="flex gap-2 shrink-0 self-start">
                  <Button size="sm" variant="outline" onClick={() => openEditC(c)} className="rounded-full border-[#A39171]/40">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => deleteC(c.id)} className="rounded-full border-[#E07A5F]/40 text-[#E07A5F] hover:bg-[#E07A5F] hover:text-white">
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            ))}
            {contentItems.filter((c) => c.type === contentType).length === 0 && (
              <p className="text-[#A39171]">No {contentType === "case_report" ? "case reports" : "articles"} yet.</p>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl bg-[#FDFBF7] border-[#A39171]/25 rounded-3xl p-8 max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-[#2D2623]">
              {editing?.id ? "Edit post" : "New post"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={savePost} className="space-y-4 mt-2">
            <div>
              <Label className="text-sm text-[#5C524E] mb-2 block">Title *</Label>
              <Input data-testid="post-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white/70 border-[#A39171]/30 rounded-xl h-12" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-[#5C524E] mb-2 block">Category</Label>
                <Input data-testid="post-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-white/70 border-[#A39171]/30 rounded-xl h-12" />
              </div>
              <div>
                <Label className="text-sm text-[#5C524E] mb-2 block">Author</Label>
                <Input data-testid="post-author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="bg-white/70 border-[#A39171]/30 rounded-xl h-12" />
              </div>
            </div>
            <ImageUploadField
              label="Cover image"
              testId="post-cover"
              value={form.cover_image}
              onChange={(url) => setForm({ ...form, cover_image: url })}
            />
            <div>
              <Label className="text-sm text-[#5C524E] mb-2 block">Excerpt *</Label>
              <Textarea data-testid="post-excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="bg-white/70 border-[#A39171]/30 rounded-xl min-h-[80px]" />
            </div>
            <div>
              <Label className="text-sm text-[#5C524E] mb-2 block">Content *</Label>
              <Textarea data-testid="post-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="bg-white/70 border-[#A39171]/30 rounded-xl min-h-[200px]" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button data-testid="post-save" type="submit" disabled={saving} className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-white px-8 h-12">
                {saving ? "Saving…" : editing?.id ? "Update Post" : "Publish Post"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setEditing(null)} className="rounded-full border-[#A39171]/40 px-8 h-12">
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Testimonial editor */}
      <Dialog open={!!tEditing} onOpenChange={(o) => !o && setTEditing(null)}>
        <DialogContent className="max-w-2xl bg-[#FDFBF7] border-[#A39171]/25 rounded-3xl p-8 max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-[#2D2623]">
              {tEditing?.id ? "Edit testimonial" : "New testimonial"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={saveT} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-[#5C524E] mb-2 block">Name (or initials) *</Label>
                <Input data-testid="testimonial-name" value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} className="bg-white/70 border-[#A39171]/30 rounded-xl h-12" placeholder="R. K." />
              </div>
              <div>
                <Label className="text-sm text-[#5C524E] mb-2 block">Location</Label>
                <Input data-testid="testimonial-location" value={tForm.location} onChange={(e) => setTForm({ ...tForm, location: e.target.value })} className="bg-white/70 border-[#A39171]/30 rounded-xl h-12" placeholder="Bhavnagar" />
              </div>
            </div>
            <div>
              <Label className="text-sm text-[#5C524E] mb-2 block">Role / Context</Label>
              <Input data-testid="testimonial-role" value={tForm.role} onChange={(e) => setTForm({ ...tForm, role: e.target.value })} className="bg-white/70 border-[#A39171]/30 rounded-xl h-12" placeholder="Anxiety & Panic — Recovered" />
            </div>
            <div>
              <Label className="text-sm text-[#5C524E] mb-2 block">Quote *</Label>
              <Textarea data-testid="testimonial-quote" value={tForm.quote} onChange={(e) => setTForm({ ...tForm, quote: e.target.value })} className="bg-white/70 border-[#A39171]/30 rounded-xl min-h-[160px]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-[#5C524E] mb-2 block">Rating (1–5)</Label>
                <Input data-testid="testimonial-rating" type="number" min="1" max="5" value={tForm.rating} onChange={(e) => setTForm({ ...tForm, rating: Number(e.target.value) || 5 })} className="bg-white/70 border-[#A39171]/30 rounded-xl h-12" />
              </div>
              <div>
                <Label className="text-sm text-[#5C524E] mb-2 block">Display order (lower = first)</Label>
                <Input data-testid="testimonial-order" type="number" value={tForm.order} onChange={(e) => setTForm({ ...tForm, order: Number(e.target.value) || 100 })} className="bg-white/70 border-[#A39171]/30 rounded-xl h-12" />
              </div>
            </div>
            <div>
              <Label className="text-sm text-[#5C524E] mb-2 block">Shown on page</Label>
              <select
                data-testid="testimonial-category"
                value={tForm.category}
                onChange={(e) => setTForm({ ...tForm, category: e.target.value })}
                className="w-full bg-white/70 border border-[#A39171]/30 rounded-xl h-12 px-3 text-[#2D2623]"
              >
                <option value="general">General (Home page)</option>
                <option value="sexology">Sexology page</option>
                <option value="deaddiction">Deaddiction page</option>
                <option value="neuromodulation">Neuromodulation page</option>
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <Button data-testid="testimonial-save" type="submit" disabled={saving} className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-white px-8 h-12">
                {saving ? "Saving…" : tEditing?.id ? "Update" : "Add Testimonial"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setTEditing(null)} className="rounded-full border-[#A39171]/40 px-8 h-12">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Press editor */}
      <Dialog open={!!prEditing} onOpenChange={(o) => !o && setPrEditing(null)}>
        <DialogContent className="max-w-2xl bg-[#FDFBF7] border-[#A39171]/25 rounded-3xl p-8 max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-[#2D2623]">
              {prEditing?.id ? "Edit press item" : "New press item"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={savePr} className="space-y-4 mt-2">
            <div>
              <Label className="text-sm text-[#5C524E] mb-2 block">Eyebrow / Source line</Label>
              <Input data-testid="press-eyebrow" value={prForm.eyebrow} onChange={(e) => setPrForm({ ...prForm, eyebrow: e.target.value })} className="bg-white/70 border-[#A39171]/30 rounded-xl h-12" placeholder="Divya Bhaskar · 10 Sep 2025" />
            </div>
            <div>
              <Label className="text-sm text-[#5C524E] mb-2 block">Title *</Label>
              <Input data-testid="press-title" value={prForm.title} onChange={(e) => setPrForm({ ...prForm, title: e.target.value })} className="bg-white/70 border-[#A39171]/30 rounded-xl h-12" />
            </div>
            <div>
              <Label className="text-sm text-[#5C524E] mb-2 block">Description</Label>
              <Textarea data-testid="press-description" value={prForm.description} onChange={(e) => setPrForm({ ...prForm, description: e.target.value })} className="bg-white/70 border-[#A39171]/30 rounded-xl min-h-[120px]" />
            </div>
            <ImageUploadField
              label="Press image *"
              testId="press-image"
              value={prForm.image_url}
              onChange={(url) => setPrForm({ ...prForm, image_url: url })}
            />
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-sm text-[#5C524E] mb-2 block">Publication</Label>
                <Input data-testid="press-publication" value={prForm.publication} onChange={(e) => setPrForm({ ...prForm, publication: e.target.value })} className="bg-white/70 border-[#A39171]/30 rounded-xl h-12" />
              </div>
              <div>
                <Label className="text-sm text-[#5C524E] mb-2 block">Size</Label>
                <select
                  data-testid="press-size"
                  value={prForm.size}
                  onChange={(e) => setPrForm({ ...prForm, size: e.target.value })}
                  className="w-full bg-white/70 border border-[#A39171]/30 rounded-xl h-12 px-3 text-[#2D2623]"
                >
                  <option value="lg">Large (7-col wide)</option>
                  <option value="md">Medium (5-col tall)</option>
                  <option value="wide-light">Wide — light</option>
                  <option value="wide-dark">Wide — dark</option>
                </select>
              </div>
              <div>
                <Label className="text-sm text-[#5C524E] mb-2 block">Order</Label>
                <Input data-testid="press-order" type="number" value={prForm.order} onChange={(e) => setPrForm({ ...prForm, order: Number(e.target.value) || 100 })} className="bg-white/70 border-[#A39171]/30 rounded-xl h-12" />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button data-testid="press-save" type="submit" disabled={saving} className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-white px-8 h-12">
                {saving ? "Saving…" : prEditing?.id ? "Update" : "Add Press Item"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setPrEditing(null)} className="rounded-full border-[#A39171]/40 px-8 h-12">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Case Report / Article editor */}
      <Dialog open={!!cEditing} onOpenChange={(o) => !o && setCEditing(null)}>
        <DialogContent className="max-w-2xl bg-[#FDFBF7] border-[#A39171]/25 rounded-3xl p-8 max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-[#2D2623]">
              {cEditing?.id
                ? `Edit ${cForm.type === "case_report" ? "case report" : "article"}`
                : `New ${cForm.type === "case_report" ? "case report" : "article"}`}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={saveC} className="space-y-4 mt-2">
            <div>
              <Label className="text-sm text-[#5C524E] mb-2 block">Type *</Label>
              <select
                data-testid="content-type"
                value={cForm.type}
                onChange={(e) => setCForm({ ...cForm, type: e.target.value })}
                className="w-full bg-white/70 border border-[#A39171]/30 rounded-xl h-12 px-3 text-[#2D2623]"
              >
                <option value="case_report">Case Report</option>
                <option value="article">Article / Publication</option>
              </select>
            </div>
            <div>
              <Label className="text-sm text-[#5C524E] mb-2 block">Title *</Label>
              <Input data-testid="content-title" value={cForm.title} onChange={(e) => setCForm({ ...cForm, title: e.target.value })} className="bg-white/70 border-[#A39171]/30 rounded-xl h-12" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-[#5C524E] mb-2 block">Tag / Category</Label>
                <Input data-testid="content-tag" value={cForm.tag} onChange={(e) => setCForm({ ...cForm, tag: e.target.value })} className="bg-white/70 border-[#A39171]/30 rounded-xl h-12" placeholder="rTMS · Depression" />
              </div>
              <div>
                <Label className="text-sm text-[#5C524E] mb-2 block">Author</Label>
                <Input data-testid="content-author" value={cForm.author} onChange={(e) => setCForm({ ...cForm, author: e.target.value })} className="bg-white/70 border-[#A39171]/30 rounded-xl h-12" />
              </div>
            </div>
            <ImageUploadField
              label="Cover image"
              testId="content-cover"
              value={cForm.cover_image}
              onChange={(url) => setCForm({ ...cForm, cover_image: url })}
            />
            <div>
              <Label className="text-sm text-[#5C524E] mb-2 block">External URL (optional)</Label>
              <Input data-testid="content-external" value={cForm.external_url} onChange={(e) => setCForm({ ...cForm, external_url: e.target.value })} className="bg-white/70 border-[#A39171]/30 rounded-xl h-12" placeholder="Link to published journal / PDF" />
            </div>
            <div>
              <Label className="text-sm text-[#5C524E] mb-2 block">Excerpt *</Label>
              <Textarea data-testid="content-excerpt" value={cForm.excerpt} onChange={(e) => setCForm({ ...cForm, excerpt: e.target.value })} className="bg-white/70 border-[#A39171]/30 rounded-xl min-h-[80px]" />
            </div>
            <div>
              <Label className="text-sm text-[#5C524E] mb-2 block">Full Content *</Label>
              <Textarea data-testid="content-body" value={cForm.content} onChange={(e) => setCForm({ ...cForm, content: e.target.value })} className="bg-white/70 border-[#A39171]/30 rounded-xl min-h-[220px]" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button data-testid="content-save" type="submit" disabled={saving} className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-white px-8 h-12">
                {saving ? "Saving…" : cEditing?.id ? "Update" : "Publish"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setCEditing(null)} className="rounded-full border-[#A39171]/40 px-8 h-12">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {/* Instagram highlight editor */}
      <Dialog open={!!iEditing} onOpenChange={(o) => !o && setIEditing(null)}>
        <DialogContent className="max-w-2xl bg-[#FDFBF7] border-[#A39171]/25 rounded-3xl p-8 max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-[#2D2623]">
              {iEditing?.id ? "Edit Instagram post" : "New Instagram post"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={saveI} className="space-y-4 mt-2">
            <ImageUploadField
              label="Post image *"
              testId="insta-image"
              value={iForm.image_url}
              onChange={(url) => setIForm({ ...iForm, image_url: url })}
            />
            <div>
              <Label className="text-sm text-[#5C524E] mb-2 block">Caption</Label>
              <Textarea data-testid="insta-caption" value={iForm.caption} onChange={(e) => setIForm({ ...iForm, caption: e.target.value })} className="bg-white/70 border-[#A39171]/30 rounded-xl min-h-[90px]" placeholder="One or two lines describing the post" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <Label className="text-sm text-[#5C524E] mb-2 block">Link to the post</Label>
                <Input data-testid="insta-url" value={iForm.post_url} onChange={(e) => setIForm({ ...iForm, post_url: e.target.value })} className="bg-white/70 border-[#A39171]/30 rounded-xl h-12" placeholder="https://www.instagram.com/p/..." />
              </div>
              <div>
                <Label className="text-sm text-[#5C524E] mb-2 block">Order</Label>
                <Input data-testid="insta-order" type="number" value={iForm.order} onChange={(e) => setIForm({ ...iForm, order: Number(e.target.value) || 100 })} className="bg-white/70 border-[#A39171]/30 rounded-xl h-12" />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button data-testid="insta-save" type="submit" disabled={saving} className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-white px-8 h-12">
                {saving ? "Saving…" : iEditing?.id ? "Update" : "Add Post"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setIEditing(null)} className="rounded-full border-[#A39171]/40 px-8 h-12">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Enquiry reply editor */}
      <Dialog open={!!replyTo} onOpenChange={(o) => !o && setReplyTo(null)}>
        <DialogContent className="max-w-xl bg-[#FDFBF7] border-[#A39171]/25 rounded-3xl p-8">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-[#2D2623]">Reply privately</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#5C524E] bg-[#F4EFE6] rounded-xl p-4 whitespace-pre-wrap">
            {replyTo?.question}
          </p>
          <p className="text-xs text-[#A39171]">Will be emailed to {replyTo?.contact}</p>
          <form onSubmit={sendReply} className="space-y-4">
            <Textarea
              data-testid="enquiry-reply-text"
              value={replyText}
              onChange={(ev) => setReplyText(ev.target.value)}
              placeholder="Write your reply. Keep it clear, kind and free of any identifying details."
              className="bg-white/70 border-[#A39171]/30 rounded-xl min-h-[180px]"
              maxLength={4000}
            />
            <div className="flex gap-3">
              <Button data-testid="enquiry-reply-send" type="submit" disabled={saving} className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-white px-8 h-12">
                {saving ? "Sending…" : "Send Reply"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setReplyTo(null)} className="rounded-full border-[#A39171]/40 px-8 h-12">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
